import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { type TypeCommon } from '../../types/common';
import { handleError } from '../../utils/common.utils';
import { LOCAL_STORAGE_KEY } from '../../constants/common.constants';
import {
  checkUserExistApi,
  resentApi,
  verifyCognitoApi,
  verifyEmailApi,
  verifyLinkApi,
  verifyUserApi,
} from '../../services/common.service';

export type InitialValue = {
  loading: boolean;
  error: object;
  user: TypeCommon;
  token: string;
};

const initialState: InitialValue = {
  error: {},
  loading: false,
  user: JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY.USER_DATA) || '{}'),
  token: '',
};

export const verifyLink = createAsyncThunk(
  'common/verify-link',
  async (
    params: {
      key: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await verifyLinkApi(params);
      return response?.data;
    } catch (error) {
      return handleError(error as TypeCommon, rejectWithValue);
    }
  }
);

export const checkUserExist = createAsyncThunk(
  'common/check-user-exist',
  async (
    params: {
      search: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await checkUserExistApi(params);
      return response?.data;
    } catch (error) {
      return handleError(error as TypeCommon, rejectWithValue);
    }
  }
);

export const verifyCognito = createAsyncThunk(
  'common/verify-cognito',
  async (
    body: {
      username: string;
      email: string;
      password: string;
      phone_number: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await verifyCognitoApi(body);
      return response?.data;
    } catch (error) {
      return handleError(error as TypeCommon, rejectWithValue);
    }
  }
);

export const verifyEmail = createAsyncThunk(
  'common/verify-email',
  async (
    body: {
      username: string;
      confirmation_code: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await verifyEmailApi(body);
      return response?.data;
    } catch (error) {
      return handleError(error as TypeCommon, rejectWithValue);
    }
  }
);

export const resent = createAsyncThunk(
  'common/resent',
  async (
    body: {
      username: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await resentApi(body);
      return response?.data;
    } catch (error) {
      return handleError(error as TypeCommon, rejectWithValue);
    }
  }
);

export const verifyUser = createAsyncThunk(
  'common/verify-user',
  async (
    body: {
      username: string;
      email: string;
      phone_number: string;
      first_name: string;
      last_name: string;
      first_name_yomi: string;
      last_name_yomi: string;
      organization_id: string;
      registered_group: string;
      role: string;
      active: string;
      service_id: string;
      link_id: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await verifyUserApi(body);
      return response?.data;
    } catch (error) {
      return handleError(error as TypeCommon, rejectWithValue);
    }
  }
);

export const common = createSlice({
  name: 'common',
  initialState,
  reducers: {
    setUserDataInfo: (state, action: PayloadAction<TypeCommon>) => {
      state.user = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addMatcher(
      isAnyOf(
        verifyLink.fulfilled,
        checkUserExist.fulfilled,
        verifyCognito.fulfilled,
        verifyEmail.fulfilled,
        resent.fulfilled,
        verifyUser.fulfilled
      ),
      state => {
        return {
          ...state,
          loading: false,
        };
      }
    );
    builder.addMatcher(
      isAnyOf(
        verifyLink.rejected,
        checkUserExist.rejected,
        verifyCognito.rejected,
        verifyEmail.rejected,
        resent.rejected,
        verifyUser.rejected
      ),
      (state, action) => {
        return {
          ...state,
          loading: false,
          error: {
            message: action.payload,
          },
        };
      }
    );
    builder.addMatcher(
      isAnyOf(
        verifyLink.pending,
        checkUserExist.pending,
        verifyCognito.pending,
        verifyEmail.pending,
        resent.pending,
        verifyUser.pending
      ),
      state => {
        return {
          ...state,
          loading: true,
        };
      }
    );
  },
});

export const { setUserDataInfo } = common.actions;
export default common.reducer;
